/* istanbul ignore file */
import { KatalMonitoring, LogLevel, AppType, MetricDestinationSink } from "@amzn/katal-monitoring";
import { KatalMonitoringDriver } from "@amzn/katal-monitoring-aws-driver";
import { getMetaContent } from "src/utils/helpers";
import { MONS_META_TAGS, IS_BEHIND_MONS, MONS_ROUTE_BASE } from "src/constants";

const shouldLogToConsole = () =>
  process.env.NODE_ENV !== "test" && process.env.NODE_ENV !== "production";

// Prevent monitoring from making requests when not behind Mons.
const monitoringUrl = IS_BEHIND_MONS
  ? `${MONS_ROUTE_BASE}/app-monitoring`
  : undefined;

const monitoringBackendApiOverride = IS_BEHIND_MONS
  ? undefined
  : () => Promise.resolve();

// Prevent console errors when outside of mons.
const metricContext = IS_BEHIND_MONS
  ? undefined
  : {
    site: "unknown",
    serviceName: "AffordabilityWebsite",
  };

const getMonitor = (): KatalMonitoring => {
  return new KatalMonitoring({
    monitoringDriver: new KatalMonitoringDriver({
      url: monitoringUrl,
      logToConsole: shouldLogToConsole(),
      backendApiOverride: monitoringBackendApiOverride,
    }),
    appType: IS_BEHIND_MONS ? AppType.MONS : AppType.NON_MONS,
    logThreshold: LogLevel.info,
    destinationSinks: [MetricDestinationSink.CLOUDWATCH, MetricDestinationSink.SUSHI],
    metricContext,
  });
};

const monitor: KatalMonitoring = getMonitor();
monitor.addErrorListener();

const METRIC_NAMES = {
  APP_INITIALIZED: "App.Initialized",
  ROOT_ERROR_BOUNDARY_ENCOUNTER: "RootErrorBoundary.Encounter",
  ROUTE_ERROR_BOUNDARY_ENCOUNTER: "RouteErrorBoundary.Encounter",
  COMPONENT_ERROR_BOUNDARY_ENCOUNTER: "ComponentErrorBoundary.Encounter",
  NAVIGATION_CLICK: "Navigation.Click",
  API_FETCH: 'Api.Fetch',
  CLICKSTREAM: 'Clickstream',
  PAGE_VIEW: 'Page.View'
};

const PAGE_IDS = {
  HOME: 'Home',
  LIST_CAMPAIGNS: 'ListCampaigns',
  CREATE_CAMPAIGN: 'CreateCampaign',
  VIEW_CAMPAIGN: 'ViewCampaign',
  MODIFY_CAMPAIGN: 'ModifyCampaign',
  COMPETITOR_PRICING: 'CompetitorPricing',
  UNKNOWN: 'Unknown',
} as const;

const LOG_PREFIX = "[affordability_website]";

let requestId: string | undefined;

const logError = (
  message: string,
  context?: Record<string, unknown> | undefined
) => {
  if (!requestId) {
    // Add MONS request ID as context to aid in debugging.
    requestId = getMetaContent(MONS_META_TAGS.REQUEST_ID);
  }
  monitor.error(`${LOG_PREFIX}: ${message}`, {
    ...(requestId && { requestId }),
    ...context,
  });
};

const trackClick = (
  target: string,
  context?: Record<string, unknown>
) => {
  monitor.count(METRIC_NAMES.CLICKSTREAM, 1, {
    target,
    methodName: "Click",
    ...context,
  });
};

export { monitor, logError, trackClick, METRIC_NAMES, PAGE_IDS, LOG_PREFIX };
